/**
 * Utility functions for file downloads and CloudFront cookie management
 */

/**
 * Triggers a file download in the browser
 * @param url - Pre-signed URL or download URL
 * @param fileName - Name for the downloaded file
 */
export const downloadFile = (url: string, fileName: string): void => {
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  link.target = '_blank'; // Safari fix: open in new tab if download fails
  link.rel = 'noopener noreferrer'; // Security best practice

  // Safari fix: append to DOM before clicking
  document.body.appendChild(link);
  link.click();

  // Clean up
  setTimeout(() => {
    document.body.removeChild(link);
  }, 100);
};

/**
 * Triggers a browser download from a presigned URL without opening a new tab.
 * Relies on the URL's Content-Disposition (attachment) from the server.
 */
export function downloadPresignedFile(url: string, fileName: string): void {
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  link.rel = 'noopener noreferrer';

  document.body.appendChild(link);
  link.click();

  setTimeout(() => {
    document.body.removeChild(link);
  }, 100);
}

/**
 * Fetches a remote file as a blob and saves it locally without opening a new tab.
 * Use for cross-origin presigned URLs where the `download` attribute alone is ignored.
 */
export async function downloadFileFromUrl(url: string, fileName: string): Promise<void> {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Failed to download file (${response.status})`);
  }

  const blob = await response.blob();
  const objectUrl = URL.createObjectURL(blob);

  try {
    const link = document.createElement('a');
    link.href = objectUrl;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

/**
 * Set CloudFront signed cookies for localhost (for Vite proxy to forward)
 * In local dev, these cookies are set for localhost and forwarded by Vite proxy to CloudFront
 * @param cookies - Object containing cookie name-value pairs
 */
export const setCloudFrontCookiesForLocalhost = (cookies: Record<string, string>): void => {
  try {
    // Set cookies for localhost domain
    // Sent with Vite CloudFront proxies (/cloudfront-signed, /legacy-sample-preview, etc.)
    Object.entries(cookies).forEach(([name, value]) => {
      // Set cookie without domain (defaults to current domain = localhost)
      // path=/ makes it available to all routes
      // Note: Removed 'secure' flag for local development (localhost is HTTP)
      document.cookie = `${name}=${value}; path=/; samesite=lax`;
    });
  } catch (error) {
    console.error('[Cookie Setter] ❌ Error setting cookies:', error);
    throw error;
  }
};
